import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Container from 'react-bootstrap/Container';
import { biblioQueryReferenceCurie } from '../../actions/biblioActions';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Spinner from "react-bootstrap/Spinner";
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import { api } from "../../api";
import Modal from 'react-bootstrap/Modal';

import { faTrash } from '@fortawesome/free-solid-svg-icons'

import RowDivider from './RowDivider';
import {RowDisplayString} from './BiblioDisplay';
import ModalGeneric from './ModalGeneric';

import {
  downloadReferencefile,
  fileUploadResult,
  fetchReferenceFiles,
  setFileUploadingCount,
  setFileUploadingShowModal,
  setFileUploadingShowSuccess
} from '../../actions/biblioActions';
import { mergeQueryAtp } from '../../actions/mergeActions';

import {useDropzone} from 'react-dropzone';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

const BiblioFileManagement = () => {
  const dispatch = useDispatch();
  const fileUploadingIsUploading = useSelector(state => state.biblio.fileUploadingCount) > 0;
  const referenceJsonLive = useSelector(state => state.biblio.referenceJsonLive);
  const [workflowRefreshTrigger, setWorkflowRefreshTrigger] = useState(false);
    
  const accessToken = useSelector(state => state.isLogged.accessToken);

  // determine accessLevel
  const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
  const testerMod = useSelector(state => state.isLogged.testerMod);
  const cognitoDeveloper = useSelector(state => state.isLogged.cognitoDeveloper);
  const cognitoObserver = useSelector(state => state.isLogged.cognitoObserver);
  let accessLevel = cognitoMod;

  if (testerMod !== 'No') {
    accessLevel = testerMod;
  } else if (cognitoDeveloper) {
    accessLevel = 'developer';
  }

  if (accessLevel === 'developer') {
    if (process.env.REACT_APP_DEV_OR_STAGE_OR_PROD === 'prod') {
      accessLevel = 'No';
    } else {
      accessLevel = null;
    }
  }

  // get mods in corpus from 'mod_corpus_associations'
  const modCorpusAssociations = referenceJsonLive['mod_corpus_associations'] || [];

  const modsInCorpus = modCorpusAssociations
    .filter(item => item.corpus === 'inside_corpus')
    .map(item => item.mod_abbreviation);

  // determine if user can upload files. Observers reach this view for reading
  // and downloading (their accessLevel is their sponsoring MOD), but are
  // read-only: no upload, and no add-to-corpus either (SCRUM-6431).
  let canUploadFiles = false;

  if (!cognitoObserver && accessLevel !== 'No') {
    if (accessLevel === null || modsInCorpus.includes(accessLevel)) {
      canUploadFiles = true;
    }
  }

  const handleFileStatusChange = () => {
      setWorkflowRefreshTrigger(prev => !prev);
      dispatch(biblioQueryReferenceCurie(referenceJsonLive.curie));
  };
    
  return (
    <>
      <Container>
        <BiblioCitationDisplay key="filemanagementCitationDisplay" />
        <AlertDismissibleFileUploadSuccess />
        {fileUploadingIsUploading ? <Spinner animation={"border"} /> : null}
        {canUploadFiles ? (
          <>
            <FileUpload main_or_supp="main" />
            <FileUpload main_or_supp="supplement" />
          </>
        ) : !cognitoObserver ? (
          <AddToCorpus accessLevel={accessLevel} referenceCurie={referenceJsonLive.curie} />
        ) : null}
        <OpenAccess />
        <Workflow workflowRefreshTrigger={workflowRefreshTrigger} />
        <RowDivider />
        <FileEditor onFileStatusChange={handleFileStatusChange}/>
      </Container>
    </>
  );

}

const AddToCorpus = ({ accessLevel, referenceCurie }) => {
  const [alert, setAlert] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const accessToken = useSelector(state => state.isLogged.accessToken);
  const dispatch = useDispatch();

  const addToCorpus = () => {
    const url = `/reference/add_to_corpus/${accessLevel}/${referenceCurie}`;
    api.post(url).then(res => {
      setAlert("Paper added to corpus successfully!");
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
        dispatch(biblioQueryReferenceCurie(referenceCurie));
      }, 2000);
    }).catch(error => {
      setAlert("Error adding to corpus");
      setShowAlert(true);
    });
  };

  return (
    <>
      <Row className="justify-content-center mt-2 mb-2">
        <Col xs="auto">
          <div
            className="form-control biblio-button text-center"
            type="button"
            onClick={addToCorpus}
            style={{
              cursor: 'pointer',
              padding: '5px 20px',
            }}
          >
            Add this paper to corpus
          </div>
        </Col>
      </Row>
      {showAlert && alert && <Alert variant={alert.includes("Error") ? "danger" : "success"}>{alert}</Alert>}
    </>
  );
};

const Workflow = ({ workflowRefreshTrigger }) => {
  const dispatch = useDispatch();
  const referenceJsonLive = useSelector(state => state.biblio.referenceJsonLive);
  // Observers are read-only: no workflow transitions (SCRUM-6431).
  const cognitoObserver = useSelector(state => state.isLogged.cognitoObserver);
  const referenceCurie = referenceJsonLive["curie"]
  const accessToken = useSelector(state => state.isLogged.accessToken);
  const [alert, setAlert] = useState(false);
  let [showAlert, setShowAlert] = useState(false);
  const ateamResults = useSelector(state => state.merge.ateamResults);
  const atpParents = ['ATP:0000140'];
  const atpOntology = useSelector(state => state.merge.atpOntology);

  const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
  const testerMod = useSelector(state => state.isLogged.testerMod);
  const cognitoDeveloper = useSelector(state => state.isLogged.cognitoDeveloper);
  let accessLevel = cognitoMod;

  if (testerMod !== 'No') {
    accessLevel = testerMod;
  }
  // Workflow accessLevel cannot be developer, is mod-specific

  useEffect(() => {
    // Fetch workflow data whenever the trigger changes
    dispatch(biblioQueryReferenceCurie(referenceJsonLive.curie));
  }, [dispatch, referenceJsonLive.curie, workflowRefreshTrigger]);
   
  const mods = useSelector(state => state.app.mods);

  const atpMappings = {
    '': 'Pick file status',
    'ATP:0000134': 'files uploaded',
    'ATP:0000135': 'file unavailable',
    'ATP:0000139': 'file upload in progress',
    'ATP:0000141': 'file needed',
  };
  Object.entries(atpOntology['ATP:0000140']).map(
    ([atp, obj]) => (atpMappings[atp] = obj['name'])
  );

  if ( (ateamResults === 0) && (accessToken) ) {
    dispatch(mergeQueryAtp(accessToken, atpParents));
  }

  const deriveModFileStatus = (wfTags) => {
    const modFileStatus =  {};
    mods.map(
      (mod, index) =>
	(modFileStatus[mod] = {
	  'workflow_tag_id': '',
	  'reference_workflow_tag_id': '',
	    'atpName': ''
	})
    );
    if (!wfTags) { return modFileStatus; }
    for (const [index, wfTag] of wfTags.entries()) {
      const reference_workflow_tag_id = wfTag['reference_workflow_tag_id'];
      let atp = ''; let atpName = '';
      if ('workflow_tag_id' in wfTag && wfTag['workflow_tag_id'] !== null && wfTag['workflow_tag_id'] !== '') {
        atp = wfTag['workflow_tag_id'];
        if (atp in atpMappings) { atpName = atpMappings[atp]; }
      }
      if (atpName !== '' && 'mod_abbreviation' in wfTag && wfTag['mod_abbreviation'] !== null && wfTag['mod_abbreviation'] !== '') {
        const mod = wfTag['mod_abbreviation'];
        modFileStatus[mod] = { 'workflow_tag_id': atp, 'reference_workflow_tag_id': reference_workflow_tag_id, 'atpName': atpName };
      }
    }
    return modFileStatus;
  }

  const modFileStatus = deriveModFileStatus(referenceJsonLive["workflow_tags"]);
  if (!(accessLevel in modFileStatus)) {
    return (
      <>
        <Row key='workflowFileStatus'>
          <Col className="Col-general Col-display Col-display-left" lg={{ span: 2 }}>workflow</Col>
          <Col className="Col-general Col-display Col-display-right" lg={{ span: 10 }}>Your cognito credentials must belong to a mod</Col>
        </Row>
      </>
    );
  }

  // currently we can only transition from "file upload in progress" to "file unavailable"
  const isMainPDFuploaded = modFileStatus[accessLevel]['atpName'] === 'files uploaded';
  const isDeveloperWithoutTester = cognitoDeveloper === true && testerMod === 'No';
  const hideFileUnavailableButton = cognitoObserver || isMainPDFuploaded || isDeveloperWithoutTester || (modFileStatus[accessLevel]['atpName'] !== 'file needed' && modFileStatus[accessLevel]['atpName'] !== 'file upload in progress');
  const hideInProgressButton = cognitoObserver || isMainPDFuploaded || isDeveloperWithoutTester || (modFileStatus[accessLevel]['atpName'] !== 'file needed' && modFileStatus[accessLevel]['atpName'] !== 'file unavailable');

  const handleWorkflowTransition = (newStatusId, alertMessage) => {
    let postData = {
      curie_or_reference_id: referenceCurie,
      mod_abbreviation: accessLevel,
      new_workflow_tag_atp_id: newStatusId,
      transition_type: 'manual',
    };
    return api
      .post('/workflow_tag/transition_to_workflow_status', postData)
      .then(res => {
        setAlert(alertMessage);
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
          dispatch(biblioQueryReferenceCurie(referenceCurie));
        }, 2000);
      })
      .catch(error => {
        setAlert(error.message);
        setShowAlert(true);
      });
  };

  const handleFileUnavailableClick = async () => {
    if (modFileStatus[accessLevel]['atpName'] === 'file needed') {
      await handleWorkflowTransition('ATP:0000139', "Transitioned to 'file in progress' status!");
      await handleWorkflowTransition('ATP:0000135', "Transitioned to 'file unavailable' status!");
    } else {
      await handleWorkflowTransition('ATP:0000135', "Transitioned to 'file unavailable' status!");
    }
  };

  const handleInProgressClick = async () => {
    handleWorkflowTransition('ATP:0000139', "Transitioned to 'file in progress' status!");
  };


  return (
      <>
        <Row key='workflowFileStatus'>
          <Col className="Col-general Col-display Col-display-left" lg={{ span: 2 }}>workflow</Col>
          <Col className="Col-general Col-display Col-display-right" lg={{ span: 10 }}>
            <Container>
              {!hideFileUnavailableButton && (
                <span
                  className="form-control biblio-button"
                  type="submit"
                  onClick={handleFileUnavailableClick}
                  style={{ width: '160px', display: 'inline', position: 'relative', top: '.3rem', left: '.1rem' }}
                >
                  file unavailable
                </span>
              )}
              {!hideInProgressButton && (
                <><span
                  className="form-control biblio-button"
                  type="submit"
                  onClick={handleInProgressClick}
                  style={{ width: '160px', display: 'inline', position: 'relative', top: '.3rem', left: '.9rem' }}
                >
                  in progress
                </span>&nbsp;&nbsp;</>
              )}
              <RowDivider />
              <Row key="fileStatusDisplay">
                {mods.map((mod, index) => (
                  <Col className="Col-general Col-display" lg={{span: 4}} style={{ display: 'flex'}} key={`fileStatusDisplay ${mod}`}>{mod} {modFileStatus[mod]['atpName']}</Col>)) }
              </Row>
            </Container>
          </Col>
        </Row>
        {showAlert && alert && <Alert variant="success">{alert}</Alert>}
      </>
  );
}

const OpenAccess = () => {

  const dispatch = useDispatch();
  // Observers never get the license editor — read-only span always (SCRUM-6431).
  const cognitoObserver = useSelector(state => state.isLogged.cognitoObserver);
  const [licenseData, setLicenseData] = useState([]);
  const [resourceLicenses, setResourceLicenses] = useState({ curie: null, list: [] });
  const [newLicense, setNewLicense] = useState('');
  const [showPermissionDetails, setShowPermissionDetails] = useState(false);
  const referenceJsonLive = useSelector(state => state.biblio.referenceJsonLive);
  const referenceCurie = referenceJsonLive["curie"]
  const resourceCurie = referenceJsonLive["resource_curie"];
  const accessToken = useSelector(state => state.isLogged.accessToken);
  const [alert, setAlert] = useState(false);
  let [showAlert, setShowAlert] = useState(false);

  const fetchLicenseData = async () => {
    try {
      const result = await api.get("/copyright_license/all");
      setLicenseData(result.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchLicenseData().finally();
  }, []);

  const licenseName = referenceJsonLive["copyright_license_name"];
  const licenseToShow = licenseName ? `${licenseName} (${referenceJsonLive["copyright_license_open_access"] ? "open access" : "not open access"})` : '';
  const imagePermission = referenceJsonLive["effective_image_permission"] || {};
  const imagePermissionName = imagePermission["image_permission_name"] || null;
  const imagePermissionUrl = imagePermission["permission_url"] || null;
  const permissionDocUrl = imagePermission["permission_doc_url"] || null;
  const imagePermissionSource = imagePermission["source"];
  const permissionTextRaw = imagePermission["permission_text"] || null;
  const startYear = imagePermission["start_year"] || null;
  const endYear = imagePermission["end_year"] || null;
  const notes = imagePermission["notes"] || null;
  const canDisplayImages = imagePermission["can_display_images"] ? "can display images" : "cannot display images";

  // the license_list restriction only applies when the backend resolved image permission to the
  // resource's open access license (which includes the publication_year >= license_start_year check)
  const resourceLicenseApplies = imagePermissionSource === "resource_open_access" &&
      imagePermission["can_display_images"] === true;

  useEffect(() => {
    let cancelled = false;
    // clear first so a list from the previous resource never restricts the current reference
    setResourceLicenses({ curie: null, list: [] });
    if (!resourceCurie || !resourceLicenseApplies) return;
    const fetchResourceLicenseList = async () => {
      try {
        const result = await api.get("/resource/" + resourceCurie);
        if (!cancelled) { setResourceLicenses({ curie: resourceCurie, list: result.data["license_list"] || [] }); }
      } catch (error) {
        console.log(error);
        if (!cancelled) { setResourceLicenses({ curie: resourceCurie, list: [] }); }
      }
    }
    fetchResourceLicenseList().finally();
    return () => { cancelled = true; };
  }, [resourceCurie, resourceLicenseApplies]);

  useEffect(() => {
    setNewLicense('');
  }, [referenceCurie]);

  // Extract values for permission text placeholders
  const getPublicationYear = () => {
    if (referenceJsonLive["date_published"]) {
      const match = referenceJsonLive["date_published"].match(/^\d{4}/);
      return match ? match[0] : null;
    }
    return null;
  };

  const getArticleUrl = () => {
    const crossRefs = referenceJsonLive["cross_references"] || [];
    const doiRef = crossRefs.find(ref => ref.curie && ref.curie.startsWith("DOI:"));
    if (doiRef) {
      return `https://doi.org/${doiRef.curie.replace("DOI:", "")}`;
    }
    const pmidRef = crossRefs.find(ref => ref.curie && ref.curie.startsWith("PMID:"));
    if (pmidRef) {
      return `https://pubmed.ncbi.nlm.nih.gov/${pmidRef.curie.replace("PMID:", "")}`;
    }
    return null;
  };

  const journalName = referenceJsonLive["resource_title"] || null;
  const publisherName = referenceJsonLive["publisher"] || null;
  const publicationYear = getPublicationYear();
  const articleUrl = getArticleUrl();

  // Extract publisher name from permission name as fallback (e.g., "American Chemical Society (ACS) - Permission Granted" -> "American Chemical Society")
  const extractPublisherFromPermissionName = (permName) => {
    if (!permName) return null;
    // Split by " - " and take the first part, then remove parenthetical abbreviations
    const parts = permName.split(' - ');
    if (parts.length > 0) {
      return parts[0].replace(/\s*\([^)]*\)\s*$/, '').trim();
    }
    return null;
  };

  // Separate publisher name and URL for proper placeholder replacement
  const effectivePublisherName = publisherName || extractPublisherFromPermissionName(imagePermissionName);
  const effectivePublisherUrl = imagePermissionUrl;

  // Replace placeholders in permission text with actual values
  const formatPermissionText = (text) => {
    if (!text) return null;
    let formatted = text;

    // Replace Journal URL/name
    if (journalName) {
      formatted = formatted.replace(/<Journal_URL>/g, journalName);
    }

    // Replace Article URL (handle typos in templates)
    if (articleUrl) {
      formatted = formatted.replace(/<Article_URL>/g, articleUrl);
      formatted = formatted.replace(/<Articlel_URL>/g, articleUrl);
      formatted = formatted.replace(/<Link to Article_URL>/g, articleUrl);
    }

    // Replace Publication year
    if (publicationYear) {
      formatted = formatted.replace(/<Publication_year>/g, publicationYear);
    }

    // Replace Publisher name (human-readable name only)
    if (effectivePublisherName) {
      formatted = formatted.replace(/<Publisher_name>/g, effectivePublisherName);
      formatted = formatted.replace(/<Publisher_URL>/g, effectivePublisherName);
    } else if (effectivePublisherUrl) {
      // Fallback to URL only for <Publisher_URL> placeholder
      formatted = formatted.replace(/<Publisher_URL>/g, effectivePublisherUrl);
    }

    // Strip any remaining unreplaced placeholders to avoid showing raw tags to users
    formatted = formatted.replace(/<[A-Za-z_ ]+>/g, '').trim();
    // Clean up any double spaces left after stripping
    formatted = formatted.replace(/\s{2,}/g, ' ');

    return formatted;
  };

  const permissionText = formatPermissionText(permissionTextRaw);

  // Check if this is an Alliance-granted permission that allows image display
  // Only show green label when can_display_images is true (not for subset permissions)
  const hasAlliancePermission = imagePermission["can_display_images"] && imagePermissionName && (
    imagePermissionName.toLowerCase().includes('permission granted') ||
    imagePermissionName.toLowerCase().includes('blanket permission') ||
    imagePermissionName.toLowerCase().includes('contract') ||
    imagePermissionName.toLowerCase().includes('display allowed')
  );

  // Show source label based on where permission came from
  const getSourceLabel = () => {
    if (imagePermissionSource === "reference_open_access") return "reference open access";
    if (imagePermissionSource === "resource_open_access") return "resource open access";
    if (imagePermissionSource === "resource_image_permission") {
      // Return link if URL is available, otherwise just the name
      if (imagePermissionUrl) {
        return (
          <a href={imagePermissionUrl} target="_blank" rel="noopener noreferrer">
            {imagePermissionName}
          </a>
        );
      }
      return imagePermissionName;
    }
    return null;
  };
  const imagePermissionSourceLabel = getSourceLabel();

  // Format year range for display
  const getYearRange = () => {
    if (startYear && endYear) return `${startYear}-${endYear}`;
    if (startYear) return `${startYear}-present`;
    if (endYear) return `up to ${endYear}`;
    return null;
  };

  let lastUpdatedBy = ''
  if (referenceJsonLive["copyright_license_last_updated_by"] && referenceJsonLive["copyright_license_last_updated_by"] !== 'default_user') {
    lastUpdatedBy = referenceJsonLive["copyright_license_last_updated_by"];
  }

  // restrict the pulldown to the licenses on the reference's resource; the curie check keeps a list
  // fetched for a previous resource from restricting the current reference
  const normalizeLicense = (name) => (name || '').trim().toLowerCase();
  const resourceLicenseSet = new Set(resourceLicenses.list.map(normalizeLicense));
  let applicableLicenses = licenseData;
  if (resourceLicenseApplies && resourceLicenses.curie === resourceCurie && resourceLicenseSet.size > 0) {
    const restricted = licenseData.filter(x => resourceLicenseSet.has(normalizeLicense(x.name)));
    if (restricted.length > 0) { applicableLicenses = restricted; }
  }
  let licenseNames = ['Pick a license', ...applicableLicenses.map(x => x.name)]
  if (licenseName !== '' && lastUpdatedBy !== '') {
    licenseNames.push('No license');
  }

  const addLicense = (e) => {
    if (!newLicense || newLicense === 'Pick a license' || newLicense === licenseName) return false;
    // never submit a selection the current option list no longer offers
    if (!licenseNames.includes(newLicense)) return false;
    let license = newLicense.replace(' ', '+')
    const url = "/reference/add_license/" + referenceCurie + "/" + license;
    api.post(url).then((res) => {
      setAlert("License Updated!");
      setShowAlert(true);
      setNewLicense('');
      setTimeout(() => {
        setShowAlert(false);
        dispatch(biblioQueryReferenceCurie(referenceCurie));
      }, 2000);
    }).catch((error) => {
      setAlert(error);
      setShowAlert(true);
    });
  }

  return (
      <>
        <Row key='open_access'>
          <Col className="Col-general Col-display Col-display-left" lg={{ span: 2 }}>open access</Col>
          <Col className="Col-general Col-display Col-display-right" lg={{ span: 10 }} style={{ display: 'flex', alignItems: 'center'}}>
            {lastUpdatedBy !== '' ?
                <div>
                  <span style={{ marginRight: '10px' }}>{licenseToShow}</span>
                  updated by <span style={{ margin: '0 10px' }}>{lastUpdatedBy}</span>
                </div>
                :
                null
            }
            {/* Observers: read-only, and only when the lastUpdatedBy block
                above hasn't already shown the license (it renders for any
                curator-licensed reference, so an unconditional span here
                would double the license text) (SCRUM-6431). */}
            {cognitoObserver ?
                (lastUpdatedBy === '' ? <span>{licenseToShow}</span> : null)
                :
             (licenseToShow !== '' && lastUpdatedBy === '') ?
                <span>{licenseToShow}</span>
                :
                <>
                  <Form.Control as='select' id='license' name='license' style={{width: "10em"}} value={newLicense} onChange={(e) => setNewLicense(e.target.value)} >
                    {licenseNames.map((optionValue, index) => (
                        <option value={optionValue} defaultValue={licenseToShow !== '' ? licenseName : null} key={index}>{optionValue}</option>
                    ))}
                  </Form.Control>
                  &nbsp;
                  <div className={`form-control biblio-button`} type="submit" onClick={(e) => addLicense(e)} style={{ width: '160px' }}>{licenseToShow !== '' ? "Update" : "Add"} License</div>
                </>
            }
          </Col>
        </Row>
        <Row key='image_permission'>
          <Col className="Col-general Col-display Col-display-left" lg={{ span: 2 }}>image permission</Col>
          <Col className="Col-general Col-display Col-display-right" lg={{ span: 10 }}>
            <div>
              <span><strong>{canDisplayImages}</strong></span>
              {imagePermissionSourceLabel && <span style={{ marginLeft: '10px', color: '#666' }}>({imagePermissionSourceLabel})</span>}
              {imagePermissionSource === "resource_image_permission" && imagePermissionName && (
                <button
                  className="button-to-link"
                  style={{ marginLeft: '10px', fontSize: '0.9em' }}
                  onClick={() => setShowPermissionDetails(!showPermissionDetails)}
                >
                  {showPermissionDetails ? 'hide details' : 'show details'}
                </button>
              )}
            </div>
            {showPermissionDetails && imagePermissionSource === "resource_image_permission" && imagePermissionName && (
              <div style={{
                marginTop: '10px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                fontSize: '0.9em'
              }}>
                {hasAlliancePermission && (
                  <div style={{ marginBottom: '8px', color: '#28a745', fontWeight: 'bold' }}>
                    Alliance-Granted Permission
                  </div>
                )}
                <div style={{ marginBottom: '5px' }}>
                  <strong>Permission Type:</strong> {imagePermissionName}
                </div>
                {getYearRange() && (
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Year Range:</strong> {getYearRange()}
                  </div>
                )}
                {permissionText && (
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Attribution Text:</strong>
                    <div style={{
                      marginTop: '5px',
                      padding: '8px',
                      backgroundColor: '#fff',
                      border: '1px solid #e9ecef',
                      borderRadius: '3px',
                      fontStyle: 'italic'
                    }}>
                      {permissionText}
                    </div>
                  </div>
                )}
                {notes && (
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Notes:</strong> {notes}
                  </div>
                )}
                {imagePermissionUrl && (
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Publisher Permissions Page:</strong>{' '}
                    <a href={imagePermissionUrl} target="_blank" rel="noopener noreferrer">
                      {imagePermissionUrl}
                    </a>
                  </div>
                )}
                {permissionDocUrl && (
                  <div>
                    <strong>Permission Documentation:</strong>{' '}
                    <a href={permissionDocUrl} target="_blank" rel="noopener noreferrer">
                      View Permission Document
                    </a>
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
        {showAlert && alert && <Alert variant="success">{alert}</Alert>}
      </>
  );
}

const FileUpload = ({main_or_supp}) => {
  const dispatch = useDispatch();
  const referenceCurie = useSelector(state => state.biblio.referenceCurie);
  const accessToken = useSelector(state => state.isLogged.accessToken);
  const fileUploadingShowModal = useSelector(state => state.biblio.fileUploadingShowModal);
  const fileUploadingModalText = useSelector(state => state.biblio.fileUploadingModalText);

  const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
  const testerMod = useSelector(state => state.isLogged.testerMod);
  const cognitoDeveloper = useSelector(state => state.isLogged.cognitoDeveloper);
  let accessLevel = cognitoMod;

  if (testerMod !== 'No') { accessLevel = testerMod; }
    else if (cognitoDeveloper) { accessLevel = 'developer'; }
  // FileUpload accessLevel can be developer to see all files and upload as PMC

  if (accessLevel === 'developer') {
    if (process.env.REACT_APP_DEV_OR_STAGE_OR_PROD === 'prod') {
      accessLevel = 'No';
    } else {
      // This means that developers upload files with PMC (all) access in dev environment only - no access on prod
      accessLevel = null;
    }
  }

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileToReUpload, setFileToReUpload] = useState(null);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleConfirmUpload = () => {
    console.log('Hello Confirm upload');	
    setShowConfirmModal(false);
    if (fileToReUpload) {
	uploadFile(fileToReUpload, true);
	setFileToReUpload(null); // reset fileToReUpload
    }
  };

  const handleCancelUpload = () => {
    console.log('Hello Cancel upload');
    setShowConfirmModal(false);
    setFileToReUpload(null);
    dispatch(setFileUploadingCount(0)); // reset file uploading count
  };

  const uploadFile = (file, uploadIfAlreadyConverted = false) => {
    const formData = new FormData();
    formData.append("file", file);
    let fileName = "";
    let fileExtension = "";
    if (file.name.toLowerCase().endsWith(".tar.gz")) {
      fileName = file.name.split(".").slice(0, -2).join(".");
      fileExtension = file.name.split(".").slice(-2).join(".");
    } else {
      fileName = file.name.split(".").slice(0, -1).join(".");
      fileExtension = file.name.split(".").pop();
    }

    let url = `/reference/referencefile/file_upload/?reference_curie=${referenceCurie}&display_name=${fileName}&file_class=${main_or_supp}&file_publication_status=final&file_extension=${fileExtension}&is_annotation=false`;
    if (accessLevel !== null) {
      url += `&mod_abbreviation=${accessLevel}`;
    }
    if (uploadIfAlreadyConverted) {
      url += "&upload_if_already_converted=True";
    }

    if (accessLevel !== 'No') {
      api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      }).then((res) => {
        dispatch(fileUploadResult(`<strong>${file.name}</strong>`, 'success<br/>'));
        dispatch(setFileUploadingCount(0)); // Reset file uploading count on success
      }).catch((error) => {
        console.error('Upload Error:', error);
        const errorDetail = error.response && error.response.data && error.response.data.detail
          ? error.response.data.detail
          : 'An error occurred during the file upload';
        if (errorDetail.includes("process is complete before uploading any files")) {
	  setErrorMessage(errorDetail);
	  setIsErrorMessage(true);
	  setShowConfirmModal(true);
	  setTimeout(() => {
	    setShowConfirmModal(false);
	    setErrorMessage("");
          }, 8000);
        } else if (error.response && error.response.status === 422 && errorDetail.includes("File already converted to text")) {
          setFileToReUpload(file);
          setShowConfirmModal(true);
        } else {
          dispatch(fileUploadResult(`<strong>${file.name}</strong><br/>`, `${errorDetail}<br/>`));
        }
	dispatch(setFileUploadingCount(0));
      });
    }
  };


  // https://react-dropzone.js.org/
  const onDrop = useCallback((acceptedFiles) => {
    dispatch(setFileUploadingCount(acceptedFiles.length));

    acceptedFiles.forEach((file) => {
      uploadFile(file);
    });
  }, [accessLevel, dispatch, accessToken, main_or_supp, referenceCurie]);

  const {getRootProps, getInputProps} = useDropzone({onDrop})

  return (
      <>
        <ModalGeneric showGenericModal={fileUploadingShowModal}
                      genericModalHeader="File Upload Result"
                      genericModalBody={fileUploadingModalText.replaceAll(". ", ".<br/>")}
                      onHideAction={setFileUploadingShowModal(false)} />
        <Row key={main_or_supp} >
          <Col className="Col-general Col-display Col-display-left" lg={{ span: 2 }}>{main_or_supp} file</Col>
          <Col className="Col-general Col-display Col-display-right" lg={{ span: 10 }} style={{ display: 'flex', alignItems: 'center'}}>
            { (() => {
              if (accessLevel !== 'No') {
                return (
                  <div className="dropzone" {...getRootProps()} >
                    <input {...getInputProps()} />
                    <p>Drag and drop {main_or_supp} file here, or click to select files</p>
                  </div>
                ) }
              else { return ( <div>You must be in a Cognito curator group to upload files in production</div>) } } )() }
          </Col>
        </Row>

        <Modal show={showConfirmModal} onHide={handleCancelUpload}>
          <Modal.Header closeButton>
	     <Modal.Title>{isErrorMessage ? "File Processing In Progress" : "Confirm Re-upload"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
	     {isErrorMessage ? errorMessage : "Do you really want to re-upload the file and re-run entity extraction pipelines?"}
          </Modal.Body>
          <Modal.Footer>
	    {!isErrorMessage && (
              <>
                <Button variant="secondary" onClick={handleCancelUpload}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmUpload}>Confirm</Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </>
  );
}

const AlertDismissibleFileUploadSuccess = () => {
  const dispatch = useDispatch();
  const fileUploadingShowSuccess = useSelector(state => state.biblio.fileUploadingShowSuccess);
  if (fileUploadingShowSuccess) {
    setTimeout(() => {
      dispatch(setFileUploadingShowSuccess(false))
    }, 2000);
    return (
      <Alert variant="success" onClose={() => dispatch(setFileUploadingShowSuccess(false))} dismissible>
        <Alert.Heading>All files uploaded</Alert.Heading>
      </Alert>
    );
  } else { return null; }
}

export const BiblioCitationDisplay = ({ extraLabelContent }) => {
  const referenceJsonLive = useSelector(state => state.biblio.referenceJsonLive);
  const referenceJsonDb = useSelector(state => state.biblio.referenceJsonDb);
  const fieldName = 'citation';
  return (<RowDisplayString key={fieldName} fieldName={fieldName} referenceJsonLive={referenceJsonLive} referenceJsonDb={referenceJsonDb} extraLabelContent={extraLabelContent} />);
}


// export const reffileCompareFn = (a, b) => {
//  if (a.file_class + a.display_name + a.file_extension > b.file_class + b.display_name + b.file_extension) {
//    return 1;
//  }
//  if (a.file_class + a.display_name + a.file_extension < b.file_class + b.display_name + b.file_extension) {
//    return -1;
//  }
//  return 0;
// }

export const reffileCompareFn = (a, b) => {
  // Sort order: main first, then supplement, then others (figure, etc.) at the end
  const fileClassOrder = { 'main': 0, 'supplement': 1 };
  const orderA = fileClassOrder[a.file_class] ?? 2;
  const orderB = fileClassOrder[b.file_class] ?? 2;

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  // Within same class, sort by display_name + file_extension
  const keyA = a.display_name + a.file_extension;
  const keyB = b.display_name + b.file_extension;
  return keyA.localeCompare(keyB);
};


// Markdown conversion file classes (for main and supplement files)
const CONVERTED_FILE_CLASSES = {
  main: [
    'converted_merged_main',
    'converted_grobid_main',
    'converted_docling_main',
    'converted_marker_main'
  ],
  supplement: [
    'converted_merged_supplement',
    'converted_grobid_supplement',
    'converted_docling_supplement',
    'converted_marker_supplement'
  ]
};

// All converted file classes (for filtering). Raw tei files no longer exist —
// deleted from the DB and S3 (SCRUM-5954); only their derived markdown
// (_tei-suffixed converted rows) remains.
const ALL_CONVERTED_FILE_CLASSES = [
  ...CONVERTED_FILE_CLASSES.main,
  ...CONVERTED_FILE_CLASSES.supplement
];

// Display labels for converted file types
const CONVERTED_FILE_LABELS = {
  'converted_merged_main': 'merged',
  'converted_grobid_main': 'grobid',
  'converted_docling_main': 'docling',
  'converted_marker_main': 'marker',
  'converted_merged_supplement': 'merged',
  'converted_grobid_supplement': 'grobid',
  'converted_docling_supplement': 'docling',
  'converted_marker_supplement': 'marker'
};

// Method suffixes used in converted file display names.
// PDFX outputs use _grobid/_docling/_marker/_merged; XML-derived outputs use
// _nxml (from nXML sources) and _tei (from TEI sources). Stripping any of
// these yields the source display_name used as the grouping key.
const METHOD_SUFFIXES = ['_grobid', '_docling', '_marker', '_merged', '_nxml', '_tei'];

// Create a mapping of base display_name to converted markdown files.
// Converted files have names like "PMC123_grobid" but we need to map to "PMC123"
export const getConvertedFilesMap = (referenceFiles) => {
  const map = {};
  referenceFiles.forEach(file => {
    if (ALL_CONVERTED_FILE_CLASSES.includes(file.file_class)) {
      // Strip method suffix to get base display_name
      let baseDisplayName = file.display_name;
      for (const suffix of METHOD_SUFFIXES) {
        if (baseDisplayName.endsWith(suffix)) {
          baseDisplayName = baseDisplayName.slice(0, -suffix.length);
          break;
        }
      }
      if (!map[baseDisplayName]) {
        map[baseDisplayName] = {};
      }
      map[baseDisplayName][file.file_class] = file;
    }
  });
  return map;
};

// Get the appropriate file class list based on the reference file class.
// nXML sources are treated like main files because their markdown conversions
// carry main converted classes (e.g. converted_merged_main).
export const getConvertedFileClasses = (fileClass) => {
  if (fileClass === 'main' || fileClass === 'nXML') {
    return CONVERTED_FILE_CLASSES.main;
  } else if (fileClass === 'supplement') {
    return CONVERTED_FILE_CLASSES.supplement;
  }
  return [];
};

// Display-name method suffix of a converted file, or null when none matches.
const getMethodSuffix = (displayName) =>
  METHOD_SUFFIXES.find((suffix) => displayName.endsWith(suffix)) || null;

// Ordered {file, label} entries for the Converted Files column of a row,
// in merged, grobid, docling, marker order. Legacy tei-derived markdown
// (_tei suffix) is included on main/supplement rows.
// A main PDF and an nXML file often share the same display_name, so the class
// lists alone would show the same conversion on both rows; the _nxml suffix
// pins nXML-derived markdown to its nXML source row and keeps it off the
// main/supplement rows (and vice versa for the other suffixes).
export const getConvertedFileEntries = (referenceFile, convertedFilesMap) => {
  const convertedFiles = convertedFilesMap[referenceFile.display_name] || {};
  const wantNxmlDerived = referenceFile.file_class === 'nXML';
  return getConvertedFileClasses(referenceFile.file_class)
    .filter((fileClass) => convertedFiles[fileClass])
    .filter((fileClass) => {
      const isNxmlDerived = getMethodSuffix(convertedFiles[fileClass].display_name) === '_nxml';
      return isNxmlDerived === wantNxmlDerived;
    })
    .map((fileClass) => ({
      file: convertedFiles[fileClass],
      label: CONVERTED_FILE_LABELS[fileClass]
    }));
};

const FileEditor = ({ onFileStatusChange }) => {
  const displayOrEditor = 'display';
  const fieldName = 'referencefiles';

  const dispatch = useDispatch();
  const accessToken = useSelector(state => state.isLogged.accessToken);
  // hasAccess (below) is a READ/download predicate; observers satisfy it for
  // open-access, figure and own-MOD/PMC files but must never get the edit
  // controls (pdf_type / publication status PATCH, delete) (SCRUM-6431).
  const cognitoObserver = useSelector(state => state.isLogged.cognitoObserver);
  const loadingFileNames = useSelector(state => state.biblio.loadingFileNames);
  const referenceCurie = useSelector(state => state.biblio.referenceCurie);
  const fileUploadingShowSuccess = useSelector(state => state.biblio.fileUploadingShowSuccess);
  const referenceJsonLive = useSelector(state => state.biblio.referenceJsonLive);
  const referenceFiles = useSelector(state => state.biblio.referenceFiles);
  const referenceFilesLoading = useSelector(state => state.biblio.referenceFilesLoading);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [referencefileIdToPatch, setReferencefileIdToPatch] = useState(null);
  const [patchDataToApply, setPatchDataToApply] = useState(null);

  const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
  const testerMod = useSelector(state => state.isLogged.testerMod);
  const cognitoDeveloper = useSelector(state => state.isLogged.cognitoDeveloper);
  const accessLevel = testerMod !== 'No' ? testerMod : cognitoDeveloper ? 'developer' : cognitoMod;

  useEffect(() => {
    dispatch(fetchReferenceFiles(referenceCurie));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceCurie]);

  useEffect(() => {
    if (fileUploadingShowSuccess) {
      dispatch(fetchReferenceFiles(referenceCurie, true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadingShowSuccess, referenceCurie]);

  const patchReferencefile = (referencefileId, data) => {
    const dataToSend = {
        ...data,
        change_if_already_converted: false
    };
    api.patch("/reference/referencefile/" + referencefileId, dataToSend)
      .then((res) => {
        dispatch(fetchReferenceFiles(referenceCurie, true));
        onFileStatusChange();
      }).catch((error) => {
        console.error('Patch Error:', error);
        const errorDetail = error.response && error.response.data && error.response.data.detail
          ? error.response.data.detail
          : 'An error occurred during the file update';
        if (errorDetail.includes("File already converted to text")) {
          setReferencefileIdToPatch(referencefileId);
          setPatchDataToApply(data);
          setShowConfirmModal(true);
        } else {
          setErrorMessage(errorDetail);
          setShowErrorModal(true);
        }
      });
  }

  const handleConfirmPatch = () => {
    if (referencefileIdToPatch && patchDataToApply) {
      const dataToSend = {
        ...patchDataToApply,
        change_if_already_converted: true
      };
      api.patch("/reference/referencefile/" + referencefileIdToPatch, dataToSend)
        .then((res) => {
          dispatch(fetchReferenceFiles(referenceCurie, true));
          onFileStatusChange();
          setReferencefileIdToPatch(null);
          setPatchDataToApply(null);
          setShowConfirmModal(false);
        }).catch((error) => {
          console.error('Patch Error:', error);
          const errorDetail = error.response && error.response.data && error.response.data.detail
            ? error.response.data.detail
            : 'An error occurred during the file update';
          setErrorMessage(errorDetail);
          setShowErrorModal(true);
          setShowConfirmModal(false);
        });
    }
  };

  const handleCancelPatch = () => {
    setReferencefileIdToPatch(null);
    setPatchDataToApply(null);
    setShowConfirmModal(false);
  };

  const handleKeepManualTETtags = () => {
    setShowErrorModal(false);
  };

  const handleDeleteManualTETtags = async () => {
    try {
      await api.delete(`/topic_entity_tag/delete_manual_tets/${referenceCurie}/${accessLevel}`);
      setShowErrorModal(false);
      dispatch(fetchReferenceFiles(referenceCurie, true));
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "An unexpected error occurred.";
      console.error("Failed to delete TET tags:", err);
      setErrorMessage(errorDetail);
      setShowErrorModal(true);
    }
  };

  const deleteReferencefile = async (referencefileId) => {
    try {
      await api.delete(`/reference/referencefile/${referencefileId}`);
      dispatch(fetchReferenceFiles(referenceCurie, true));
      onFileStatusChange();
    } catch (error) {
      let errorDetail = "An unexpected error occurred while deleting the file";
      if (error.response && error.response.data && error.response.data.detail) {
        errorDetail = error.response.data.detail;
      }
      if (errorDetail.includes("Curated topic and entity tags")) {
        setErrorMessage(errorDetail);
        setShowErrorModal(true);
      } else {
        console.error("Delete error:", error);
        setErrorMessage(errorDetail);
        setShowErrorModal(true);
      }
    }
  };

  const convertedFilesMap = getConvertedFilesMap(referenceFiles);

  // Helper to render a single converted file link
  const renderConvertedFileLink = (file, label, hasAccess) => {
    if (!file) return null;
    const filename = `${file.display_name}.${file.file_extension}`;
    if (hasAccess) {
      return (
        <span key={file.referencefile_id} style={{ marginRight: '8px' }}>
          <button
            className='button-to-link'
            onClick={() => dispatch(downloadReferencefile(file.referencefile_id, filename, accessToken))}
            title={filename}
          >
            {label}
          </button>
          {loadingFileNames.has(filename) ? <Spinner animation="border" size="sm" /> : null}
        </span>
      );
    }
    return <span key={file.referencefile_id} style={{ marginRight: '8px' }} title={filename}>{label}</span>;
  };

  const getDisplayRowsFromReferenceFiles = (referenceFilesArray, hasAccess) => {
    // Read (download links) and write (edit/delete controls) separate here:
    // hasAccess keeps the downloads, canEdit gates the mutations.
    const canEdit = hasAccess && !cognitoObserver;
    return referenceFilesArray.map((referenceFile, index) => {
      const source = referenceFile.referencefile_mods.map(
        (mod) => mod.mod_abbreviation === null ? "PMC" : mod.mod_abbreviation
      ).join(", ");
      let filename = referenceFile.display_name + '.' + referenceFile.file_extension;
      const wrapStyle = { wordBreak: 'break-word', overflowWrap: 'break-word' };
      let referencefileValue = (<div style={wrapStyle}>{filename}</div>);
      if (hasAccess) {
        referencefileValue = (
          <div style={wrapStyle}>
            <button
              className='button-to-link'
              style={wrapStyle}
              onClick={() => dispatch(downloadReferencefile(referenceFile.referencefile_id, filename, accessToken))}
            >
              {filename}
            </button>
            &nbsp;{loadingFileNames.has(filename) ? <Spinner animation="border" size="sm" /> : null}
          </div>
        );
      }

      // Converted markdown files for this reference file (merged, grobid, docling, marker order)
      const convertedFileEntries = getConvertedFileEntries(referenceFile, convertedFilesMap);
      let convertedFilesDisplay = null;
      if (convertedFileEntries.length > 0) {
        convertedFilesDisplay = (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {convertedFileEntries.map(({ file, label }) => renderConvertedFileLink(file, label, hasAccess))}
          </div>
        );
      }

      return (
        <Row key={`${fieldName} ${index}`} className="Row-general" xs={2} md={4} lg={12}>
          <Col className={`Col-general Col-display-left`} lg={{ span: 2 }}>{referenceFile.file_class}</Col>
          <Col className="Col-general Col-display" lg={{ span: 3 }}>{referencefileValue}</Col>
          <Col className="Col-general Col-display" lg={{ span: 3 }}>{convertedFilesDisplay}</Col>
          <Col className="Col-general Col-display" lg={{ span: 1 }}>{source}</Col>
          <Col className="Col-general Col-display" lg={{ span: 1 }}>
            <Form.Control
              as="select"
              disabled={!canEdit}
              value={referenceFile.pdf_type ?? ''}
              onChange={(event) => {
                const newValue = event.target.value === "" ? null : event.target.value;
                if (referenceFile.pdf_type !== newValue) {
                  patchReferencefile(referenceFile.referencefile_id, { "pdf_type": newValue }, accessToken);
                }
              }}
            >
              <option></option>
              <option>pdf</option>
              <option>ocr</option>
              <option>tif</option>
              <option>lib</option>
              <option>aut</option>
              <option>html</option>
            </Form.Control>
          </Col>
          <Col className="Col-general Col-display" lg={{ span: 1 }}>
            <Form.Control
              as="select"
              disabled={!canEdit}
              value={referenceFile.file_publication_status}
              onChange={(event) => {
                const newValue = event.target.value;
                if (referenceFile.file_publication_status !== newValue) {
                  patchReferencefile(referenceFile.referencefile_id, { "file_publication_status": newValue }, accessToken);
                }
              }}
            >
              <option>prepub</option>
              <option>temp</option>
              <option>final</option>
              <option>other</option>
            </Form.Control>
          </Col>
          <Col className="Col-general Col-display-right" lg={{ span: 1 }}>
            <Button variant="outline-dark" disabled={!canEdit} onClick={() => deleteReferencefile(referenceFile.referencefile_id)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Col>
        </Row>
      );
    });
  };
          
  let rowReferencefileElements = [];
  // Filter out all converted markdown files from the main display
  const canDisplayImages = referenceJsonLive["effective_image_permission"]?.["can_display_images"] === true;
  const hasReferenceFileAccess = (referenceFile) => (
    referenceJsonLive["copyright_license_open_access"] === true ||
    (referenceFile.file_class === 'figure' && canDisplayImages) ||
    accessLevel === 'developer' ||
    referenceFile.referencefile_mods.some((mod) => mod.mod_abbreviation === accessLevel || mod.mod_abbreviation === null)
  );

  const referenceFilesWithAccess = referenceFiles.filter((referenceFile) =>
    hasReferenceFileAccess(referenceFile) &&
    !ALL_CONVERTED_FILE_CLASSES.includes(referenceFile.file_class)
  );

  const referenceFilesNoAccess = referenceFiles.filter((referenceFile) =>
    !hasReferenceFileAccess(referenceFile) &&
    !ALL_CONVERTED_FILE_CLASSES.includes(referenceFile.file_class)
  );

  referenceFilesWithAccess.sort(reffileCompareFn);
  referenceFilesNoAccess.sort(reffileCompareFn);
  rowReferencefileElements = [
    <Row key={`${fieldName} header`} className="Row-general" xs={2} md={4} lg={12}>
      <Col className="Col-general Col-display-left" lg={{ span: 2 }}><strong>File Class</strong></Col>
      <Col className="Col-general Col-display" lg={{ span: 3 }}><strong>File Name</strong></Col>
      <Col className="Col-general Col-display" lg={{ span: 3 }}><strong>Converted Files</strong></Col>
      <Col className="Col-general Col-display" lg={{ span: 1 }}><strong>Source</strong></Col>
      <Col className="Col-general Col-display" lg={{ span: 1 }}><strong>PDF Type</strong></Col>
      <Col className="Col-general Col-display" lg={{ span: 1 }}><strong>File Publication Status</strong></Col>
      <Col className="Col-general Col-display-right" lg={{ span: 1 }}><strong>Delete</strong></Col>
    </Row>,
    ...getDisplayRowsFromReferenceFiles(referenceFilesWithAccess, true),
    ...getDisplayRowsFromReferenceFiles(referenceFilesNoAccess, false)
  ];

  return (
    <>
      {referenceFilesLoading ? <Spinner animation="border" /> : rowReferencefileElements}
      <Modal show={showConfirmModal} onHide={handleCancelPatch}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>Do you really want to change the file properties and re-run text conversion pipeline?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelPatch}>Cancel</Button>
          <Button variant="primary" onClick={handleConfirmPatch}>Confirm</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {errorMessage.includes("Curated topic and entity tags")
              ? "Curated Topic Entity Tags/Mod Generated Automated Tags Found"
              : "Error Report"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage.includes("Curated topic and entity tags") ? (
            <>
              <p>{errorMessage}</p>
              <p>Would you like to:</p>
              <ul>
                <li>Keep these tags for your MOD</li>
                <li>Delete these tags</li>
              </ul>
            </>
          ) : (
            <p>{errorMessage}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {errorMessage.includes("Curated topic and entity tags") ? (
            <>
              <Button variant="secondary" onClick={handleKeepManualTETtags}>Keep Tags for My MOD</Button>
              <Button variant="danger" onClick={handleDeleteManualTETtags}>Delete Tags</Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Close</Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BiblioFileManagement;
